/**
 * @typedef {import('../../../../types/AuthContext').default} Auth
 * @typedef {import('./Agent').default} Agent
 * @typedef {import('./Agent').Props} Props
 * @typedef {import('./Agent').State} State
 * @typedef {import('./Agent').Value} Value
 * @typedef {import('./MappedAgent').default} MappedAgent
 * @typedef {import('../../../shared/AgentService/Agent').default} SharedAgent
 * @typedef {import('../../../shared/Query/Filters/Component').QueryParameters} FilterQueryParameters
 */

import { array } from '@mooveguru/js-utilities';
import { AuthContext } from '../../../../shared/AuthProvider';
import { BaseSVG } from '../../../../shared/BaseSVG';
import { BulkOptions } from '../../../../App/shared/BulkOptions/BulkOptions';
import { translate } from '../../../Internationalization';
import { withSettings } from '../../../../shared/SettingProvider/SettingProvider';
import AgentBulkInvitePopup from '../../../../shared/Popups/Agents/Invite/AgentBulkInvitePopup';
import BaseTable from '../../../../shared/BaseTable';
import bulkOptions from '../../../../config/local/bulk-options';
import BulkSelectButton from '../../../../shared/BulkSelectButton/BulkSelectButton';
import captureError from '../../../../utils/captureError';
import concatPaths from '../../../../utils/contactPaths';
import convertDateToMonthDayYear from '../../../../utils/convertDateToMonthDayYear';
import Feature from '../../../../shared/Feature';
import formatPhoneNumberForDisplay from '../../../../utils/formatPhoneNumberForDisplay';
import FormErrorMessages from '../../../../shared/Forms/Messages/FormErrorMessages';
import FormGrayMessages from '../../../../shared/Forms/Messages/FormGrayMessages';
import images from '../../../../config/local/images';
import LinkButton from '../../../../shared/LinkButton';
import Loading from '../../../../shared/Loading';
import NoContent from '../../../../shared/NoContent';
import Pagination from '../../../../shared/Pagination';
import paths from '../../../../config/local/paths';
import React from 'react';
import SuccessOrWarningMessage from '../../../../shared/Forms/Messages/SucessOrWarningMessage';
import TableButton from '../../../../shared/TableButton';
import StatusFilter from '../../../shared/Query/Filters/AgentStatusFilter/StatusFilter';
import SearchFilter from '../../../shared/Query/Filters/Search/SearchFilter';

/** @type {Set<string>} */
const groupIdList = new Set();
/** @type {Set<string>} */
const subscribedGroupIdList = new Set();

const headerKeyList = [
	'name',
	'email',
	'phone',
	'primary_group',
	'last_logged_in',
	'status',
	'invite',
	'edit',
	'edit_branding',
	'import_contacts',
];

const bulkOptionList = [
	{
		label: translate('global.pages.contacts.view.resend_invite'),
		option: bulkOptions.invite,
	},
	{
		label: translate('global.pages.contacts.view.Active'),
		option: bulkOptions.active,
	},
	{
		label: translate('global.pages.contacts.view.Disable'),
		option: bulkOptions.disable,
	},
];

/**
 * @type {string | number | null}
 */
let selectedOption;
const agentInviteFlag = 'admin:resend_agent_invite';
const agentsPerPage = 50;

/** @extends {React.Component<Props, State>} */
class ViewAgent extends React.Component {
	/** @type {Auth} */
	context = /** @type {Auth} */ ({});

	isBulkActionInFlight = false;

	isGettingAgents = false;

	shouldRefetchAgents = false;

	/** @param {Props} props */
	constructor(props) {
		super(props);

		/** @type {State} */
		this.state = {
			agentsList: [],
			currentPageNumber: 1,
			disabledInvites: [],
			errorMessage: null,
			filterQuery: {},
			filterStatus: null,
			isGettingData: true,
			isRefreshingData: false,
			isSendingInvite: false,
			lastPageNumber: 1,
			mountError: false,
			selectedAgents: [],
			selectedOption: null,
			searchQuery: {},
			successMessages: [],
			toggleSelectAll: false,
			totalRecord: 0,
		};

		this._onHandleOptionChange = this._onHandleOptionChange.bind(this);
		this._handleSubmitSearch = this._handleSubmitSearch.bind(this);
		this._handleFilterSerach = this._handleFilterSerach.bind(this);
	}

	/** @returns {Promise<void>} */
	async componentDidMount() {
		try {
			await this._setGroupList();
			await this._getAgentData();

			this.#updateSuccessMessage(
				this.props.location?.state?.message ?? ''
			);
		} catch (error) {
			this.setState({
				errorMessage: translate('global.error'),
				isGettingData: false,
				mountError: true,
			});
		}
	}

	/**
	 * @param {Props} _prevProps
	 * @param {State} prevState
	 */
	async componentDidUpdate(_prevProps, prevState) {
		if (prevState.currentPageNumber !== this.state.currentPageNumber) {
			try {
				await this._getAgentData();
			} catch (error) {
				// Errors are already captured in _getAgentData.
			}
		}
	}

	/**
	 * @param {string} feature
	 * @returns {boolean}
	 */
	#getFeature(feature) {
		return this.props?.settings?.features.get(feature) ?? false;
	}

	/**
	 * @param {string} message
	 * @returns {void}
	 */
	#updateSuccessMessage(message) {
		if (!message) {
			return;
		}

		this.setState((prevState) => {
			if (prevState.successMessages.includes(message)) {
				return null;
			}

			return {
				successMessages: [...prevState.successMessages, message],
			};
		});

		setTimeout(() => {
			this.setState((prevState) => ({
				successMessages: prevState.successMessages.filter(
					(currentMessage) => currentMessage !== message
				),
			}));
		}, 8500);
	}

	/**
	 * @param {Agent} agent
	 * @returns {Promise<void>}
	 */
	async sendAgentInvite(agent) {
		this.setState((prevState) => ({
			disabledInvites: [...prevState.disabledInvites, agent.id],
		}));

		const response = await this.sendAgentsInvite([agent.id]);

		if (!response) {
			return;
		}

		this.setState((prevState) => ({
			disabledInvites: prevState.disabledInvites.filter(
				(id) => id !== agent.id
			),
		}));

		this.#updateSuccessMessage(
			translate(
				'admin.pages.agents.invite.success',
				agent?.name?.first ?? '',
				agent?.name?.last ?? ''
			)
		);
	}

	/** @returns {Promise<void>} */
	async handleBulkOption() {
		if (this.isBulkActionInFlight) {
			return;
		}

		this.isBulkActionInFlight = true;

		const selectedOptionKey = this.state.selectedOption;
		let response;
		selectedOption = selectedOptionKey;
		let message = 'admin.pages.agents.invite.bulk';
		this.setState({ selectedOption: null });

		try {
			if (selectedOptionKey === bulkOptions.active) {
				response = await this.bulkActive(this.state.selectedAgents);
				await this._getAgentData();
				message = `${message}.${selectedOptionKey}_success`;
			} else if (selectedOptionKey === bulkOptions.invite) {
				response = await this.sendAgentsInvite(
					this.state.selectedAgents
				);
				message = `${message}.${selectedOptionKey}_success`;
			} else if (selectedOptionKey === bulkOptions.disable) {
				response = await this.bulkDisable(this.state.selectedAgents);
				await this._getAgentData();
				message = `${message}.${selectedOptionKey}_success`;
			}

			if (!response) {
				return;
			}

			this.setState({ selectedAgents: [], toggleSelectAll: false });

			this.#updateSuccessMessage(translate(message));
		} finally {
			this.isBulkActionInFlight = false;
		}
	}

	/**
	 * @param {string[]} agentIds
	 * @returns {Promise<boolean>}
	 */
	async sendAgentsInvite(agentIds) {
		this.setState({ isSendingInvite: true });

		try {
			await this.props.administratorService.resendAgentsInvite(agentIds);
			return true;
		} catch (error) {
			captureError(error);

			this.setState({
				errorMessage: translate('admin.pages.agents.invite.error'),
			});
		} finally {
			this.setState({ isSendingInvite: false });
		}

		return false;
	}

	/**
	 * @param {string[]} agentIds
	 * @returns {Promise<boolean>}
	 */
	async bulkActive(agentIds) {
		this.setState({ isSendingInvite: true });

		try {
			await this.props.administratorService.setStatusActive(agentIds);
			return true;
		} catch (error) {
			captureError(error);

			this.setState({
				errorMessage: translate('admin.pages.agents.invite.error'),
			});
		} finally {
			this.setState({ isSendingInvite: false });
		}

		return false;
	}

	/**
	 *
	 * @param {string[]} agentIds
	 * @returns {Promise<boolean>}
	 */
	async bulkDisable(agentIds) {
		this.setState({ isSendingInvite: true });

		try {
			await this.props.administratorService.setStatusDisable(agentIds);
			return true;
		} catch (error) {
			this.setState({
				errorMessage: translate('admin.pages.agents.invite.error'),
			});
		} finally {
			this.setState({ isSendingInvite: false });
		}

		return false;
	}

	/**
	 * @param {string} className
	 * @returns {JSX.Element | null}
	 */
	#bulkOptionSelect(className = '') {
		if (!this.#getFeature(agentInviteFlag)) {
			return null;
		}

		return (
			<BulkOptions
				bulkOptionList={bulkOptionList}
				className={className}
				handleOptionChange={this._onHandleOptionChange}
			/>
		);
	}

	/**
	 * @protected
	 * @param {{label: string; option: string}} selection
	 * @returns {void}
	 */
	_onHandleOptionChange(selection) {
		if (
			selection.option !== 'bulk_invite' &&
			selection.option !== 'bulk_delete' &&
			selection.option !== 'bulk_active' &&
			selection.option !== 'bulk_disable'
		) {
			return;
		}

		this.setState({ selectedOption: selection.option });
	}

	/**
	 * @protected
	 * @param {boolean} selectAll
	 * @returns {void}
	 */
	_handleSelectAll(selectAll = true) {
		if (!selectAll) {
			this.setState({ selectedAgents: [], toggleSelectAll: false });
			return;
		}

		this.setState((prevState) => ({
			selectedAgents: prevState.agentsList.map((agent) => agent.id),
			toggleSelectAll: true,
		}));
	}

	/** @returns {JSX.Element | null} */
	#renderBulkPopup() {
		/* eslint-disable-next-line react/destructuring-assignment */
		const { selectedAgents, selectedOption: currentSelectedOption } =
			this.state;

		if (
			currentSelectedOption !== bulkOptions.active &&
			currentSelectedOption !== bulkOptions.invite &&
			currentSelectedOption !== bulkOptions.disable
		) {
			return null;
		}

		if (!selectedAgents.length) {
			setTimeout(() => {
				this.setState({ selectedOption: null });
			}, 0);
			return null;
		}

		return (
			<AgentBulkInvitePopup
				onClose={() => this.setState({ selectedOption: null })}
				onSubmit={() => this.handleBulkOption()}
				selectedAgents={selectedAgents}
				selectedOption={currentSelectedOption}
			/>
		);
	}

	/**
	 * @param {Agent} agent
	 * @returns {void}
	 */
	_handleCheckBox(agent) {
		const agentsList = this.state.selectedAgents;
		const agentIndex = agentsList.indexOf(agent.id);

		let agents = [...agentsList];
		if (agentIndex === -1) {
			agents.push(agent.id);
		} else {
			agents = agents.filter((id) => id !== agent.id);
		}

		this.setState(() => ({ selectedAgents: agents }));
	}

	/**
	 * @protected
	 * @returns {Promise<void>}
	 */
	async _getAgentData() {
		/* eslint-disable-next-line react/destructuring-assignment */
		const { currentPageNumber, filterQuery } = this.state;

		if (this.isGettingAgents) {
			this.shouldRefetchAgents = true;
			return;
		}

		this.isGettingAgents = true;
		this.setState((prev) => ({
			isGettingData: prev.agentsList.length === 0,
			isRefreshingData: prev.agentsList.length > 0,
		}));

		try {
			const agents = await this.props.agentService.getAgents(
				this.context.accessToken,
				currentPageNumber,
				agentsPerPage,
				filterQuery
			);

			this.setState({
				agentsList: agents.data.map((agent) =>
					ViewAgent.#mapAgentForList(agent)
				),
				lastPageNumber: agents.pages,
				totalRecord: agents.total,
			});
		} catch (error) {
			captureError(error);
			this.setState({ errorMessage: translate('global.error') });
			throw error;
		} finally {
			this.isGettingAgents = false;
			this.setState({
				isGettingData: false,
				isRefreshingData: false,
			});

			if (this.shouldRefetchAgents) {
				this.shouldRefetchAgents = false;
				await this._getAgentData();
			}
		}
	}

	/**
	 * @param {SharedAgent} agent
	 * @returns {Agent}
	 */
	static #mapAgentForList(agent) {
		const mappedAgent = /** @type {SharedAgent & { active?: boolean }} */ (
			agent
		);
		const phoneNumbers = Array.isArray(agent.phoneNumbers)
			? agent.phoneNumbers
			: [];
		const mobilePhoneNumber =
			phoneNumbers.find((phoneNumber) => phoneNumber.type === 'mobile')
				?.value ??
			agent.phone?.mobile ??
			'';
		const landlinePhoneNumber =
			phoneNumbers.find((phoneNumber) => phoneNumber.type === 'landline')
				?.value ??
			agent.phone?.landline ??
			'';

		return {
			active:
				typeof mappedAgent.active === 'boolean'
					? mappedAgent.active
					: true,
			email: agent.email ?? agent.emailAddress ?? '',
			groups: agent.groups,
			id: agent.id,
			isSubscribed:
				agent.groups?.some((group) =>
					subscribedGroupIdList.has(group.id)
				) ?? false,
			lastLoggedInAt: agent.lastLoggedInAt ?? null,
			name: agent.name,
			phone: {
				landline: landlinePhoneNumber,
				mobile: mobilePhoneNumber,
			},
			slug: agent.slug,
		};
	}

	/**
	 * @protected
	 * @returns {Promise<void>}
	 */
	async _setGroupList() {
		const administrator =
			await this.props.administratorService.getAdministratorPersonalData();

		administrator.groups.forEach(
			/** @param {{hasSubscription?: boolean, id: string}} group*/
			(group) => {
				groupIdList.add(group.id);

				if (group.hasSubscription) {
					subscribedGroupIdList.add(group.id);
				}
			}
		);
	}

	/** @returns {Value[][]} */
	_setTableData() {
		if (!this.state.agentsList.length) {
			return [];
		}

		const filteredAgents = this.state.agentsList;
		const dataMap = this._setAgentData(filteredAgents);

		// depending on agent, some values may be empty, or n/a (especially with test data)
		const bodyData = dataMap.map(
			/**
			 * @param {MappedAgent} agent
			 * @returns {Value[]}
			 */
			(agent) => Object.values(agent).map((data) => ({ data }))
		);

		if (this.#getFeature(agentInviteFlag)) {
			this.#addInvite(filteredAgents, bodyData);
		}

		ViewAgent.#addEdit(filteredAgents, bodyData);
		ViewAgent.#addEditTheme(filteredAgents, bodyData);
		ViewAgent.#addImportContacts(filteredAgents, bodyData);

		return bodyData;
	}

	/**
	 * @returns {MappedAgent[]}
	 * @param {Agent[]} data
	 */
	_setAgentData(data) {
		return data.map(
			/**
			 * @param {Agent} agent
			 * @returns {MappedAgent}
			 */
			// eslint-disable-next-line complexity
			(agent) => ({
				/* eslint-disable sort-keys -- Properties in order of table headers */
				name: this.#getNameField(agent),
				email: agent?.email,
				phone:
					agent?.phone?.mobile || agent?.phone?.landline
						? formatPhoneNumberForDisplay(
								agent?.phone?.mobile || agent?.phone?.landline
						  )
						: null,
				primaryGroup:
					agent.groups?.find(
						(/** @type {{ isPrimary: boolean }} */ group) =>
							group.isPrimary
					)?.name ?? null,
				lastLoggedIn: agent?.lastLoggedInAt
					? convertDateToMonthDayYear(agent?.lastLoggedInAt)
					: null,
				status: agent?.active
					? translate('admin.pages.agents.view.table.active')
					: translate('admin.pages.agents.view.table.disabled'),
			})
		);
	}

	/**
	 * @param {Agent} agent
	 * @returns {JSX.Element}
	 */
	#getNameField(agent) {
		const inviteFeature = (
			<Feature fallback={null} name={agentInviteFlag}>
				<span className="form-check form-check-sm">
					<input
						checked={this.state.selectedAgents.includes(agent.id)}
						onChange={() => this._handleCheckBox(agent)}
						type="checkbox"
					/>
				</span>
			</Feature>
		);

		const subscribedFeature = (
			<Feature fallback={null} name="admin:show_premium_agent_badge">
				{!!agent?.active && !!agent?.isSubscribed && (
					<span className="badge badge-themed">
						<i className="icon">
							<BaseSVG path={images.icons.premium} />
						</i>
					</span>
				)}
			</Feature>
		);

		return (
			<div className="flex">
				{inviteFeature}
				{subscribedFeature}
				{`${agent?.name?.first} ${agent?.name?.last}`}
			</div>
		);
	}

	/**
	 * @param {Agent['groups']} groupList
	 * @returns {string | null}
	 */
	static #resolveRelatedId(groupList) {
		return groupList.find((group) => groupIdList.has(group.id))?.id ?? null;
	}

	/** @returns {null | JSX.Element} */
	#importButton() {
		if (!array.includesAny(this.context.roles, 'owner', 'admin')) {
			return null;
		}

		return (
			<LinkButton
				icon={images.icons.import}
				to={{
					pathname: paths.app.admin.agents.import,
					state: {
						referrer: `${location.pathname}${location.search}${location.hash}`,
					},
				}}
			>
				{translate('admin.pages.agents.view.import')}
			</LinkButton>
		);
	}

	/** @returns {{title: string | JSX.Element}[]} */
	#getTableHeader() {
		const bulkInvite = (
			<BulkSelectButton
				checked={this.state.toggleSelectAll}
				handleDeSelectAll={() => this._handleSelectAll(false)}
				handleSelectAll={() => this._handleSelectAll()}
			>
				{translate(`admin.pages.agents.view.table.name`)}
			</BulkSelectButton>
		);

		return headerKeyList
			.map((key) => {
				if (key === 'name') {
					return {
						title: this.#getFeature(agentInviteFlag)
							? bulkInvite
							: translate(`admin.pages.agents.view.table.name`),
					};
				}

				if (key === 'invite') {
					return {
						title: this.#getFeature(agentInviteFlag)
							? translate(`admin.pages.agents.view.table.invite`)
							: '',
					};
				}

				return {
					title: translate(`admin.pages.agents.view.table.${key}`),
				};
			})
			.filter((header) => header.title);
	}

	// eslint-disable-next-line class-methods-use-this
	#getMessageKey() {
		const messages = {
			[bulkOptions.active]: {
				heading: 'admin.pages.agents.invite.info.status.header',
				message: 'admin.pages.agents.invite.info.status.message',
			},
			[bulkOptions.invite]: {
				heading: 'admin.pages.agents.invite.info.invite.header',
				message: 'admin.pages.agents.invite.info.invite.message',
			},
			[bulkOptions.disable]: {
				heading: 'admin.pages.agents.invite.info.status.header',
				message: 'admin.pages.agents.invite.info.status.message',
			},
		};

		return (
			messages[selectedOption ?? bulkOptions.invite] ??
			messages[bulkOptions.invite]
		);
	}

	/**
	 * @protected
	 * @param {FilterQueryParameters} value
	 * @returns {void}
	 */
	_handleSubmitSearch(value) {
		this.setState(
			(prev) => ({
				filterQuery: {
					...prev.filterQuery,
					...value,
				},
				currentPageNumber: 1,
			}),
			() => this._getAgentData()
		);
	}

	/**
	 * @param {any} query
	 */
	_handleFilterSerach(query) {
		this.setState(
			(prev) => ({
				filterQuery: {
					...prev.filterQuery,
					...query,
				},
				currentPageNumber: 1,
			}),
			() => this._getAgentData()
		);
	}

	/** @returns {React.ReactElement} */
	render() {
		if (this.state.errorMessage && this.state.mountError) {
			return <NoContent />;
		}

		if (this.state.isGettingData && this.state.agentsList.length === 0) {
			return <Loading />;
		}

		return (
			<React.Fragment>
				{this.#renderBulkPopup()}
				<header className="mt-10 mb-6 md:flex justify-between items-center">
					<h1 className="hl-ms-6 mb-6 md:mb-0">
						{`${translate('admin.pages.agents.view.title')} (${
							this.state.totalRecord
						})`}
					</h1>

					<section className="button-group">
						{this.#importButton()}

						<LinkButton
							icon={images.icons.add}
							to={paths.app.admin.agents.create}
						>
							{translate('admin.pages.agents.view.add')}
						</LinkButton>
					</section>
				</header>

				<SuccessOrWarningMessage
					heading={this.props.location?.state?.heading}
					messages={this.state.successMessages}
					warning={this.props.location?.state?.warning}
				/>

				{this.state.isSendingInvite ? (
					<FormGrayMessages
						heading={translate(this.#getMessageKey().heading)}
						messages={translate(this.#getMessageKey().message)}
					/>
				) : null}

				{this.state.errorMessage ? (
					<FormErrorMessages messages={this.state.errorMessage} />
				) : null}

				<section className="md:flex justify-between items-center mb-6 sm:mb-0">
					<div>{this.#bulkOptionSelect('mb-4')}</div>

					<div className="flex gap-2 items-center flex-row-reverse">
						<StatusFilter
							onSubmit={this._handleFilterSerach}
							query={this.state.filterQuery}
						/>
						<SearchFilter
							onSubmit={this._handleSubmitSearch}
							query={this.state.filterQuery}
							search={['email', 'name.first', 'name.last']}
						/>
					</div>
				</section>

				<div className="relative">
					{this.state.isRefreshingData ? (
						<div className="absolute inset-0 z-10 flex items-start justify-center bg-white bg-opacity-70 pt-8">
							<Loading />
						</div>
					) : null}
					<div
						className={
							this.state.isRefreshingData
								? 'pointer-events-none opacity-60'
								: ''
						}
					>
						<BaseTable
							bodyData={this._setTableData()}
							errorMessage={translate(
								'admin.pages.agents.view.error'
							)}
							headerData={this.#getTableHeader()}
						/>
					</div>
				</div>

				<Pagination
					currentPageNumber={this.state.currentPageNumber}
					lastPageNumber={this.state.lastPageNumber}
					setCurrentPageNumber={(currentPageNumber) =>
						this.setState({ currentPageNumber })
					}
				/>
			</React.Fragment>
		);
	}

	/**
	 * @param {Agent[]} data
	 * @param {Value[][]} bodyData
	 * @returns {void}
	 */
	#addInvite(data, bodyData) {
		data?.forEach((agent, index) => {
			// @ts-ignore -- can be pushed
			bodyData?.[index]?.push({
				data: (
					<TableButton
						handleClick={() => this.sendAgentInvite(agent)}
						icon={images.icons.email}
					/>
				),
			});
		});
	}

	/**
	 * @param {Agent[]} data
	 * @param {Value[][]} bodyData
	 * @returns {void}
	 */
	static #addEdit(data, bodyData) {
		data?.forEach((agent, index) => {
			// @ts-ignore -- can be pushed
			bodyData?.[index]?.push({
				data: (
					<TableButton
						icon={images.icons.edit}
						to={`${paths.app.admin.agents.edit}/${agent.slug}`}
					/>
				),
			});
		});
	}

	/**
	 * @param {Agent[]} data
	 * @param {Value[][]} bodyData
	 * @returns {void}
	 */
	static #addEditTheme(data, bodyData) {
		data?.forEach((agent, index) => {
			// @ts-ignore -- can be pushed
			bodyData[index]?.push({
				data: (
					<TableButton
						icon={images.icons.theme}
						title={translate(
							'admin.pages.agents.view.table.edit_branding'
						)}
						to={concatPaths(
							paths.app.admin.agents.root,
							agent.id,
							'theme'
						)}
					/>
				),
			});
		});
	}

	/**
	 * @param {Agent[]} data
	 * @param {Value[][]} bodyData
	 * @returns {void}
	 */
	static #addImportContacts(data, bodyData) {
		data?.forEach((agent, index) => {
			// @ts-ignore -- can be pushed
			bodyData[index]?.push({
				data: (
					<TableButton
						icon={images.icons.import}
						title={translate(
							'admin.pages.agents.view.table.import_contacts'
						)}
						to={{
							pathname: concatPaths(
								paths.app.admin.agents.root,
								agent.id,
								'contacts/import'
							),
							state: {
								managerRelatedId: ViewAgent.#resolveRelatedId(
									agent.groups
								),
								referrer: `${location.pathname}${location.search}${location.hash}`,
							},
						}}
					/>
				),
			});
		});
	}
}

ViewAgent.contextType = AuthContext;

export default withSettings(ViewAgent);
